import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import type { GraphState, GraphData, GraphContext, GraphFilterMode, GraphInsights, CausalChainVisualization } from '@models/index'
import { omapApi } from '@services/omapApi'

// Viewport state for zoom/pan persistence across tab switches
interface ViewportState {
  zoom: number
  pan: { x: number; y: number }
  scrollLeft: number
  scrollTop: number
}

// Extended state with exploration context for Business Graph Tab
interface ExtendedGraphState extends GraphState {
  exploration: {
    anchorIds: string[]
    exploredIds: string[]
    filterMode: GraphFilterMode
    zoomCenter: { x: number; y: number } | null
    confidenceScore: number
    taxonomyDistribution: Record<string, number>
    bscDistribution: Record<string, number>
    insights: GraphInsights | null
    causalChainVisualization: CausalChainVisualization | null
    showCausalChain: boolean
  }
  viewport: ViewportState | null  // Persisted viewport state
}

const initialState: ExtendedGraphState = {
  data: null,
  filters: {
    taxonomyLayers: [],
    searchQuery: '',
  },
  selectedNodeId: null,
  loading: false,
  error: null,
  exploration: {
    anchorIds: [],
    exploredIds: [],
    filterMode: 'all',
    zoomCenter: null,
    confidenceScore: 0,
    taxonomyDistribution: {},
    bscDistribution: {},
    insights: null,
    causalChainVisualization: null,
    showCausalChain: false,
  },
  viewport: null,  // No saved viewport initially
}

// Async thunk to load graph data
export const loadGraphData = createAsyncThunk(
  'graph/loadGraphData',
  async (_, { rejectWithValue }) => {
    try {
      const data = await omapApi.fetchOntologyGraph()
      return data
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to load graph data')
    }
  }
)

const graphSlice = createSlice({
  name: 'graph',
  initialState,
  reducers: {
    setTaxonomyFilters: (state, action: PayloadAction<string[]>) => {
      state.filters.taxonomyLayers = action.payload
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.filters.searchQuery = action.payload
    },
    setSelectedNode: (state, action: PayloadAction<string | null>) => {
      state.selectedNodeId = action.payload
    },
    clearFilters: (state) => {
      state.filters = initialState.filters
    },
    // Business Graph Tab: Exploration state management
    setGraphContext: (state, action: PayloadAction<GraphContext>) => {
      const ctx = action.payload
      state.exploration.anchorIds = ctx.anchor_ids || []
      state.exploration.exploredIds = ctx.explored_ids || []
      state.exploration.zoomCenter = ctx.zoom_center || null
      state.exploration.confidenceScore = ctx.confidence_score || 0
      state.exploration.taxonomyDistribution = ctx.taxonomy_distribution || {}
      state.exploration.bscDistribution = ctx.bsc_distribution || {}
      state.exploration.insights = ctx.insights || null
      state.exploration.causalChainVisualization = ctx.causal_chain_visualization || null
      // P1: Restore UI state if present in context
      if (ctx.show_causal_chain !== undefined) {
        state.exploration.showCausalChain = ctx.show_causal_chain
      }
      if (ctx.filter_mode !== undefined) {
        state.exploration.filterMode = ctx.filter_mode
      }
    },
    // Toggle causal chain overlay visibility
    toggleCausalChainOverlay: (state) => {
      state.exploration.showCausalChain = !state.exploration.showCausalChain
    },
    setFilterMode: (state, action: PayloadAction<GraphFilterMode>) => {
      state.exploration.filterMode = action.payload
    },
    clearExploration: (state) => {
      state.exploration = initialState.exploration
    },
    // Save viewport state for persistence across tab switches
    saveViewport: (state, action: PayloadAction<ViewportState>) => {
      state.viewport = action.payload
    },
    // Clear viewport state (e.g., on conversation change)
    clearViewport: (state) => {
      state.viewport = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadGraphData.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loadGraphData.fulfilled, (state, action: PayloadAction<GraphData>) => {
        state.loading = false
        state.data = action.payload
        state.error = null
      })
      .addCase(loadGraphData.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  },
})

export const {
  setTaxonomyFilters,
  setSearchQuery,
  setSelectedNode,
  clearFilters,
  setGraphContext,
  setFilterMode,
  clearExploration,
  toggleCausalChainOverlay,
  saveViewport,
  clearViewport,
} = graphSlice.actions
export default graphSlice.reducer
